const React = require('react')
const { useEffect, useState, useRef } = require('react')
const { Container, Row, Col } = require('reactstrap')
const { useParams } = require('react-router-dom')
const { useBlockchainData } = require('@hooks')
const { useNetworkConfig } = require('@contexts')
const { Tabs } = require('@components')

const { TransactionDetails } = require('./details.transaction')
const { TransactionLogs } = require('./logs.transaction')
const { TransactionStates } = require('./states.transaction')
const { TransactionBlobs } = require('./blobs.transaction')
const { TransactionInternal } = require('./internal.transaction')

require('@styles/pages/transaction/index.css')

const Transaction = () => {
    const { transactionHash } = useParams()
    const { network } = useNetworkConfig()
    const { getBlock, getTransaction, getBlocks } = useBlockchainData(network.name)

    const [transactionData, setTransactionData] = useState(null)
    const [latestBlock, setLatestBlock] = useState(null)
    const [blockData, setBlockData] = useState(null)

    const [loading, setLoading] = useState(false)
    const [activeTab, setActiveTab] = useState(() => {
        const urlHash = window.location.hash.slice(1)
        return urlHash || 'overview'
    })
    const [isDetailsExpanded, setIsDetailsExpanded] = useState(false)

    const transactionRef = useRef(null)
    const tabsRef = useRef()

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true)
                setIsDetailsExpanded(false)

                const [transactionResponse, blocksResponse] = await Promise.all([
                    getTransaction(transactionHash),
                    getBlocks(1, 1)
                ])

                if (transactionResponse && blocksResponse) {
                    setTransactionData(transactionResponse)
                    setLatestBlock(blocksResponse.items[0])

                    transactionRef.current = transactionResponse
                    const [blockResponse] = await Promise.all([getBlock(transactionResponse.details.blockNumber)])

                    if (blockResponse) {
                        setBlockData(blockResponse)
                    }
                }
                setLoading(false)

                return transactionResponse
            } catch (error) {
                console.error('Error fetching data:', error)
                setLoading(false)
            }
        }
        fetchData()

        const interval = setInterval(async () => {
            if (!transactionRef.current?.details?.blockHash) {
                const [transactionResponse, blocksResponse] = await Promise.all([
                    getTransaction(transactionHash),
                    getBlocks(1, 1)
                ])

                if (transactionResponse.details?.blockHash) {
                    setTransactionData(transactionResponse)
                    setLatestBlock(blocksResponse.items[0])

                    transactionRef.current = transactionResponse
                    const [blockResponse] = await Promise.all([getBlock(transactionResponse.details.blockNumber)])
                    if (blockResponse) {
                        setBlockData(blockResponse)
                    }
                }
            } else {
                clearInterval(interval)
            }
        }, 2000)

        return () => clearInterval(interval)
    }, [transactionHash])

    return (
        <div className='page'>
            <Container>
                <Col>
                    <Row>
                        <div className='page-header'>
                            <text className='page-title'>Transaction Details</text>
                            <div className='page-title-border' />
                        </div>
                    </Row>
                    <Tabs
                        ref={tabsRef}
                        activeTab={activeTab}
                        items={[
                            { tab: 'overview', text: 'Overview' },
                            ...(transactionData?.traces.length > 0 ? [{ tab: 'internal', text: `Internal Txns` }] : []),
                            ...(transactionData?.details.logs.length > 0
                                ? [{ tab: 'eventlog', text: `Logs (${transactionData?.details.logs.length})` }]
                                : []),
                            ...(transactionData?.tokenTransfers.length > 0
                                ? [{ tab: 'statechange', text: `State` }]
                                : []),
                            ...(transactionData?.details.blobVersionedHashes.length > 0
                                ? [{ tab: 'blobs', text: `Blobs` }]
                                : [])
                        ]}
                        onTabChange={setActiveTab}
                    />
                    {activeTab === 'overview' && (
                        <TransactionDetails
                            loading={loading}
                            data={transactionData}
                            latestBlock={latestBlock}
                            blockData={blockData}
                            isDetailsExpanded={isDetailsExpanded}
                            setIsDetailsExpanded={setIsDetailsExpanded}
                            onTabChange={setActiveTab}
                        />
                    )}
                    {activeTab === 'internal' && <TransactionInternal loading={loading} data={transactionData} />}
                    {activeTab === 'eventlog' && <TransactionLogs data={transactionData} />}
                    {activeTab === 'statechange' && <TransactionStates loading={loading} data={transactionData} />}
                    {activeTab === 'blobs' && <TransactionBlobs loading={loading} data={transactionData} />}
                </Col>
            </Container>
        </div>
    )
}

module.exports = Transaction
